import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {object} ScentProduct
 * @property {number} variantId
 * @property {boolean} available
 * @property {string} code
 * @property {'M' | 'W' | 'U'} who
 * @property {string} inspired
 * @property {string} url
 * @property {string} price
 * @property {string} image
 * @property {string[]} families
 * @property {string[]} occasions
 * @property {'subtle' | 'bold'} strength
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data
 * @property {HTMLFormElement} quiz
 * @property {HTMLFieldSetElement[]} steps
 * @property {HTMLElement} progress
 * @property {HTMLButtonElement} backButton
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} results
 * @property {HTMLUListElement} cards
 * @property {HTMLButtonElement} addFourButton
 * @property {HTMLTemplateElement} cardTemplate
 * @property {HTMLInputElement} searchInput
 * @property {HTMLUListElement} searchResults
 * @property {HTMLElement} searchEmpty
 * @property {HTMLElement} liveRegion
 * @property {HTMLInputElement} [emailTags]
 *
 * @extends {Component<Refs>}
 */
class ScentPickerComponent extends Component {
  requiredRefs = ['data', 'quiz', 'steps', 'results', 'cards', 'cardTemplate', 'liveRegion'];

  /** @type {ScentProduct[]} */
  #products = [];

  /** @type {Record<string, string>} */
  #strings = {};

  /** @type {ScentProduct[]} */
  #matches = [];

  #step = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#readData();
    this.#showStep(0);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#readData();
    this.#showStep(this.#step);
  }

  #readData() {
    try {
      const data = JSON.parse(this.refs.data.textContent || '{}');
      this.#products = (data.products || []).filter(Boolean);
      this.#strings = data.strings || {};
    } catch {
      this.#products = [];
    }
  }

  /** @param {number} index */
  #showStep(index) {
    const { steps, progress, backButton, submitButton } = this.refs;
    this.#step = Math.max(0, Math.min(index, steps.length - 1));

    for (const [i, step] of steps.entries()) step.hidden = i !== this.#step;

    if (progress?.dataset.template) {
      progress.textContent = progress.dataset.template.replace('[current]', String(this.#step + 1));
    }
    if (backButton) backButton.hidden = this.#step === 0;
    if (submitButton) submitButton.hidden = this.#step < steps.length - 1;
  }

  /**
   * Moves to the next question once one is answered.
   * @param {Event} event
   */
  answer(event) {
    const step = event.target instanceof Element ? event.target.closest('fieldset') : null;
    const index = step instanceof HTMLFieldSetElement ? this.refs.steps.indexOf(step) : -1;
    if (index < 0 || index >= this.refs.steps.length - 1) return;

    this.#showStep(index + 1);
    this.refs.steps[this.#step]?.querySelector('input')?.focus();
  }

  back() {
    this.#showStep(this.#step - 1);
    this.refs.steps[this.#step]?.querySelector('input:checked, input')?.focus();
  }

  /** @param {SubmitEvent} event */
  showResults(event) {
    event.preventDefault();

    const firstUnanswered = this.refs.steps.findIndex((step) => !step.querySelector('input:checked'));
    if (firstUnanswered !== -1) {
      this.#showStep(firstUnanswered);
      this.refs.steps[firstUnanswered]?.querySelector('input')?.focus();
      return;
    }

    const answers = Object.fromEntries(new FormData(this.refs.quiz));
    this.#matches = this.#rank(/** @type {Record<string, string>} */ (answers));

    this.#renderCards(this.#matches.slice(0, 3));
    this.refs.addFourButton.hidden = this.#matches.length < 4;
    if (this.refs.emailTags) {
      this.refs.emailTags.value = `newsletter,scent-picker,scent-${answers.like}`;
    }

    this.refs.quiz.hidden = true;
    this.refs.results.hidden = false;
    this.refs.results.focus();
  }

  /**
   * Scores every in-stock fragrance against the answers, best first.
   * @param {Record<string, string>} answers
   * @returns {ScentProduct[]}
   */
  #rank({ who, when, like, strength }) {
    const forWho = who === 'him' ? ['M', 'U'] : who === 'her' ? ['W', 'U'] : ['M', 'W', 'U'];

    return this.#products
      .filter((product) => product.available && forWho.includes(product.who))
      .map((product) => {
        let score = 0;
        if (product.families[0] === like) score += 4;
        else if (product.families.includes(like)) score += 2;
        if (product.occasions.includes(when)) score += 2;
        if (product.strength === strength) score += 1;
        return { product, score };
      })
      .sort((a, b) => b.score - a.score || a.product.code.localeCompare(b.product.code))
      .map(({ product }) => product);
  }

  /** @param {ScentProduct[]} products */
  #renderCards(products) {
    const { cards, cardTemplate } = this.refs;
    cards.replaceChildren(
      ...products.map((product) => {
        const card = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
        const field = (/** @type {string} */ name) => card.querySelector(`[data-field="${name}"]`);

        const link = field('url');
        if (link instanceof HTMLAnchorElement) link.href = product.url;

        const image = field('image');
        if (image instanceof HTMLImageElement) {
          if (product.image) image.src = product.image;
          else image.remove();
        }

        const code = field('code');
        if (code) code.textContent = product.code;

        const inspired = field('inspired');
        if (inspired) inspired.textContent = (this.#strings.inspired || '[name]').replace('[name]', product.inspired);

        const price = field('price');
        if (price) price.textContent = product.price;

        const add = field('add');
        if (add instanceof HTMLButtonElement) {
          add.dataset.variantId = String(product.variantId);
          add.setAttribute('on:click', '/addOne');
        }

        return card;
      })
    );
  }

  /** @param {Event} event */
  async addOne(event) {
    const button = event.target instanceof HTMLButtonElement ? event.target : null;
    const variantId = Number(button?.dataset.variantId);
    if (!button || !variantId) return;

    button.disabled = true;
    const ok = await this.#addToCart([variantId]);
    button.disabled = false;
    this.#announce(ok ? this.#strings.added : this.#strings.error);
  }

  async addTopFour() {
    const button = this.refs.addFourButton;
    const ids = this.#matches.slice(0, 4).map((product) => product.variantId);
    if (ids.length < 4) return;

    button.disabled = true;
    const ok = await this.#addToCart(ids);
    button.disabled = false;
    this.#announce(ok ? this.#strings.addedFour : this.#strings.error);
  }

  /**
   * @param {number[]} variantIds
   * @returns {Promise<boolean>}
   */
  async #addToCart(variantIds) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: variantIds.map((id) => ({ id, quantity: 1 })),
            sections: sectionIds.join(','),
          }),
        })
      );
      const result = await response.json();
      if (!response.ok || result.status) return false;

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.sectionId ?? '', {
          source: 'scent-picker',
          itemCount: variantIds.length,
          sections: result.sections,
        })
      );
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /** @param {string | undefined} message */
  #announce(message) {
    if (!message) return;
    this.refs.liveRegion.textContent = '';
    requestAnimationFrame(() => {
      this.refs.liveRegion.textContent = message;
    });
  }

  restart() {
    this.refs.quiz.reset();
    this.refs.results.hidden = true;
    this.refs.quiz.hidden = false;
    this.#showStep(0);
    this.refs.steps[0]?.querySelector('input')?.focus();
  }

  /**
   * Shortcut for shoppers who already know the designer scent they like.
   */
  search() {
    const { searchInput, searchResults, searchEmpty } = this.refs;
    if (!searchInput || !searchResults || !searchEmpty) return;

    const query = normalise(searchInput.value);
    if (query.length < 2) {
      searchResults.hidden = true;
      searchEmpty.hidden = true;
      return;
    }

    const hits = this.#products.filter((product) => normalise(product.inspired).includes(query)).slice(0, 6);

    searchResults.replaceChildren(
      ...hits.map((product) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = product.url;
        link.textContent = `${product.code} ${(this.#strings.inspired || '[name]').replace('[name]', product.inspired)}`;
        item.append(link);
        if (!product.available && this.#strings.soldOut) item.append(` (${this.#strings.soldOut})`);
        return item;
      })
    );
    searchResults.hidden = hits.length === 0;
    searchEmpty.hidden = hits.length > 0;
  }
}

/** @param {string} text */
function normalise(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

if (!customElements.get('scent-picker-component')) {
  customElements.define('scent-picker-component', ScentPickerComponent);
}
